import { useState } from 'react';
import { Typography, Box, Button, Modal, FormControl, RadioGroup, FormControlLabel, Radio, Switch, } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { generateMultipleBarcodes } from '../../utils/helpers';
import CustomInput from '../Inputs/CustomInput';
import ErrorModal from './ErrorModal';
import HtmlTooltip from '../Tooltip/HtmlTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const BarcodeMultipleLabelsModal = (props) => {
	const { theme } = useTheme();

	const [openErrorModal, setopenErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const [size, setSize] = useState('1x2');
	const [direction, setDirection] = useState('horizontal');
	const [addExpirDate, setAddExpirDate] = useState(false);
	const [expirationDate, setExpirationDate] = useState("");
	const [expirationDateInputType, setExpirationDateInputType] = useState('text');
	// New state for barcode selection
	const [barcodeOption, setBarcodeOption] = useState('upc');

	const handleChangeSize = (event) => {
		setSize(event.target.value);
	};

	const handleChangeDirection = (event) => {
		setDirection(event.target.value);
	};

	const handleChangeBarcodeOption = (event) => {
		setBarcodeOption(event.target.value);
	};

	const handleConfirm = async () => {
		if (addExpirDate && expirationDate === "") {
			alertError("Please enter expiration date");
			return;
		}

		const formatter = new Intl.DateTimeFormat("en-US", {
			month: "2-digit",
			day: "2-digit",
			year: "numeric",
		});

		const barcodes = props.items.map(item => ({
			barcode: item[barcodeOption],
			name: item.productName
		}))

		props.setLoading(true);

		generateMultipleBarcodes(
			barcodes,
			size,
			direction,
			addExpirDate ? formatter.format(new Date(expirationDate)) : undefined
		).then(() => props.setLoading(false));

		props.setOpen(false);
	};

	const alertError = (msg) => {
		setErrorMessage(msg);
		setopenErrorModal(true);
	};

	const styles = {
		modal: {
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)',
			width: { xs: '90vw', sm: '80vw', md: '60vw', lg: '40vw' },
			maxHeight: '90vh',
			overflow: 'auto',
			bgcolor: theme.background.primary,
			borderRadius: 2,
			border: `4px solid ${theme.color.primary}`,
			boxShadow: 24,
			p: 3,
			outline: 'none',
		},
		contentContainer: {
			display: 'flex',
			flexDirection: { xs: 'column', md: 'row' },
			gap: 2,
		},
		leftColumn: {
			flex: 1,
		},
		rightColumn: {
			flex: 1,
		},
		button: {
			textTransform: 'none',
			fontFamily: theme.fontFamily,
			color: theme.text.negative,
			borderRadius: '20px',
			border: `1px solid ${theme.border.light}`,
			backgroundColor: theme.accent.success,
		},
		button2: {
			textTransform: 'none',
			borderRadius: '20px',
			backgroundColor: 'transparent',
			mr: '10px'
		},
		radioLabel: {
			fontFamily: theme.fontFamily,
			color: theme.text.primary,
			fontSize: '0.9rem',
		},
		sectionTitle: {
			fontFamily: theme.fontFamily,
			textAlign: 'left',
			color: theme.text.primary,
			fontWeight: 'bold',
			mb: '10px',
			mt: '15px',
		},
		printingTips: {
			fontSize: '0.85rem',
			color: theme.text.primary,
			mt: '5px',
			fontFamily: theme.fontFamily,
		}
	};

	return (
		<>
			<Modal open={props.open} onClose={() => props.setOpen(false)}>
				<Box sx={styles.modal}>
					<Typography variant="h5" component="h2" gutterBottom style={{ fontFamily: theme.fontFamily, textAlign: 'center', color: theme.text.primary }}>
						Print labels for all added items ({props.items.length})
					</Typography>

					<Box sx={styles.contentContainer}>
						{/* Left Column */}
						<Box sx={styles.leftColumn}>
							{/* Barcode selection */}
							<Typography sx={styles.sectionTitle}>
								Select barcode type:
							</Typography>

							<FormControl fullWidth>
								<RadioGroup
									value={barcodeOption}
									onChange={handleChangeBarcodeOption}
								>
									<FormControlLabel value="upc" control={<Radio size="small" />} label={<Typography sx={styles.radioLabel}>UPC</Typography>} />
									<Box>
										<FormControlLabel value="gtin" control={<Radio size="small" />} label={<Typography sx={styles.radioLabel}>GTIN</Typography>} />
										<HtmlTooltip
											customtheme={theme}
											title={
												<Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
													GTIN barcodes encoded in the ITF format
												</Typography>
											}
											arrow
											placement="top"
											slotProps={{
												popper: {
													modifiers: [
														{
															name: 'offset',
															options: {
																offset: [0, -10],
															},
														},
													],
												},
											}}
										>
											<InfoOutlinedIcon size={10} sx={{ color: theme.color.primary, cursor: 'pointer', }} />
										</HtmlTooltip>
									</Box>
								</RadioGroup>
							</FormControl>

							{/* Direction selection */}
							<Typography sx={styles.sectionTitle}>
								Select direction of labels:
							</Typography>

							<FormControl fullWidth>
								<RadioGroup
									value={direction}
									onChange={handleChangeDirection}
								>
									<FormControlLabel value="horizontal" control={<Radio size="small" />} label={<Typography sx={styles.radioLabel}>Horizontal</Typography>} />
									<FormControlLabel value="vertical" control={<Radio size="small" />} label={<Typography sx={styles.radioLabel}>Vertical</Typography>} />
								</RadioGroup>
							</FormControl>
						</Box>
						<Box sx={styles.rightColumn}>
							<Typography sx={styles.sectionTitle}>
								Select size of labels:
							</Typography>

							<FormControl fullWidth>
								<RadioGroup
									value={size}
									onChange={handleChangeSize}
								>
									<FormControlLabel value="A4" control={<Radio size="small" />} label={<Typography sx={styles.radioLabel}>A4 (63.5 mm x 38.1 mm labels)</Typography>} />
									<FormControlLabel value="1x2" control={<Radio size="small" />} label={<Typography sx={styles.radioLabel}>1x2 Inches (25.4 mm x 50.8 mm labels)</Typography>} />
									<FormControlLabel value="US_Letter" control={<Radio size="small" />} label={<Typography sx={styles.radioLabel}>US Letter (1 inch x 2.625 inch labels)</Typography>} />
								</RadioGroup>
							</FormControl>

							<Typography sx={{ ...styles.sectionTitle, mt: '20px' }}>
								Add expiration date
							</Typography>
							<Switch
								checked={addExpirDate}
								onChange={(event) => setAddExpirDate(event.target.checked)}
								inputProps={{ 'aria-label': 'controlled' }}
								size="small"
							/>

							{addExpirDate && (
								<CustomInput
									sx={{ width: '100%', mt: '5px' }}
									size="small"
									label="Expiration Date"
									value={expirationDate}
									onChange={(e) => {
										setExpirationDate(e.target.value);
										if (e.target.value) {
											setExpirationDateInputType('date');
										}
									}}
									onFocus={() => setExpirationDateInputType('date')}
									onBlur={() => {
										if (!expirationDate) {
											setExpirationDateInputType('text');
										}
									}}
									type={expirationDateInputType}
									placeholder="mm/dd/yyyy"
									InputProps={{
										inputProps: {
											max: "2030-12-31"
										}
									}}
								/>
							)}
						</Box>
					</Box>

					<Typography sx={{ ...styles.sectionTitle, mt: '15px', mb: '5px' }}>
						Printing Tips:
					</Typography>
					<Box>
						<Typography sx={styles.printingTips}>• Use appropriate paper size and print settings</Typography>
						<Typography sx={styles.printingTips}>• Use printer with 300+ DPI resolution</Typography>
						<Typography sx={styles.printingTips}>• Perform regular testing and maintenance</Typography>
						<Typography sx={styles.printingTips}>• Test printed barcode quality regularly</Typography>
					</Box>

					{/* Buttons */}
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
						<Button sx={styles.button2} onClick={() => props.setOpen(false)} >
							<Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, textDecoration: 'underline' }}>cancel</Typography>
						</Button>
						<Button variant="contained" sx={styles.button} onClick={handleConfirm} >
							Print
						</Button>
					</Box>
				</Box>
			</Modal>

			<ErrorModal open={openErrorModal} setOpen={setopenErrorModal} message={errorMessage} />
		</>
	);
};

export default BarcodeMultipleLabelsModal;